import { CountryCode, ProductCodeKey, Subscription, SubscriptionPlan } from '@dayetopia/library';
import { ProductRawOption } from '@contracts';
import checkResStatus from '@utils/checkResStatus';
import getOmsUrl from '@utils/getOmsUrl';
import { logger } from '@utils/logger';

export const getPlanPrice = (
  price: number,
  subscription: SubscriptionPlan,
  country: CountryCode,
  shippingPrice?: number,
  calculateBillingPrice?: boolean,
  freeShippingRequirement?: number
) => {
  // Apply discount only if calculateBillingPrice is true, otherwise use price as-is
  const billingPrice = calculateBillingPrice ? getBillingPriceForSubscription(subscription, price, country) : price;

  // Check free shipping threshold: if billingPrice >= threshold, shipping is free
  const appliedShipping = freeShippingRequirement && billingPrice >= freeShippingRequirement ? 0 : shippingPrice ?? 0;

  return billingPrice + appliedShipping;
};

export const getBillingPriceForSubscription = (subscription: SubscriptionPlan, boxPrice: number, country: CountryCode) => {
  const discountPercent = getDiscountPercent(subscription, country);
  const itemPrice = boxPrice - (boxPrice * discountPercent) / 100;
  // No rounding for TamponBox or Pads - only round for other products
  const shouldRound = subscription.productCode !== ProductCodeKey.TamponBox && subscription.productCode !== ProductCodeKey.Pads;
  const discountedPrice = shouldRound ? Math.round(itemPrice * 20) / 20 : itemPrice;
  return (discountedPrice * subscription.subscriptionInterval.length) / subscription.billing.installments;
};

export const getSubscriptionPlans = async (productCode: string) => {
  const omsUrl = getOmsUrl();
  const url = `${omsUrl}/ecommerce/subscriptions/${productCode}`;
  const options = {
    method: 'GET'
  };

  try {
    const res = await fetch(url, options);
    const data = checkResStatus(res);
    return await data;
  } catch (err) {
    logger.debug('Subscription plan fetch error', err);
    throw err;
  }
};

export const getDiscountPercent = (subscription: SubscriptionPlan, country: CountryCode) => {
  return subscription?.discountPercent?.[country] ?? subscription?.discountPercent?.default ?? 0;
};

export const isSubscriptionBundle = (subscription: Subscription) => {
  const { variants } = subscription;

  if (!variants) {
    return false;
  }

  return Object.keys(variants).some((key) => key.includes('_bundle_'));
};

/**
 * Checks if product options contain bundle SKUs (e.g., tampon_box_retail_bundle_*)
 * Uses the same '_bundle_' pattern as isSubscriptionBundle for consistency
 *
 * @param productOptions - Array of product options from Prismic
 * @returns true if any option contains a bundle SKU
 */
export const hasBundleSkuInOptions = (productOptions?: ProductRawOption[]): boolean => {
  if (!productOptions?.length) {
    return false;
  }

  return productOptions.some((option) => {
    const oneOffSku = option.one_off_sku?.text ?? '';
    const subscriptionSku = option.subscription_sku?.text ?? '';

    return oneOffSku.includes('_bundle_') || subscriptionSku.includes('_bundle_');
  });
};
